import React from "react"
import ReactMarkdown from "react-markdown"
import { CircularProgress, TextField, Button, Alert, Select, MenuItem, FormControl, InputLabel } from "@mui/material"

import { FaThumbsDown, FaThumbsUp } from "react-icons/fa"
import { IoMdClose } from "react-icons/io"
import "./Bot.css"
import rehypeRaw from "rehype-raw"
import { sendFeedback, sendPositiveFeedback } from "./utils"

function LinkRenderer(props) {
  return (
    <a href={props.href} target="_blank" rel="noreferrer">
      {props.children}
    </a>
  )
}

const FeedbackForm = ({ onClose, onSubmit, loading, error }) => {
  const [feedbackText, setFeedbackText] = React.useState("")
  const [category, setCategory] = React.useState("General Feedback")
  const [severity, setSeverity] = React.useState("Medium")
  const [email, setEmail] = React.useState("")

  const handleSubmit = () => {
    onSubmit({
      feedbackText,
      category,
      severity,
      email: email || undefined
    })
  }

  return (
    <div className="div-feedback">
      {error && (
        <Alert sx={{ width: "fit-content", margin: "auto" }} severity="error">
          {error}
        </Alert>
      )}
      <div className="feedback-form">
        <div className="feedback-header">
          <h3>Submit Feedback</h3>
          <IoMdClose
            style={{ cursor: "pointer" }}
            color="#000"
            size={22}
            onClick={onClose}
          />
        </div>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={category}
            label="Category"
            onChange={(e) => setCategory(e.target.value)}
          >
            <MenuItem value="General Feedback">General Feedback</MenuItem>
            <MenuItem value="Bug Report">Bug Report</MenuItem>
            <MenuItem value="Incorrect Information">Incorrect Information</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Severity</InputLabel>
          <Select
            value={severity}
            label="Severity"
            onChange={(e) => setSeverity(e.target.value)}
          >
            <MenuItem value="Low">Low</MenuItem>
            <MenuItem value="Medium">Medium</MenuItem>
            <MenuItem value="High">High</MenuItem>
            <MenuItem value="Critical">Critical</MenuItem>
          </Select>
        </FormControl>

        <TextField
          fullWidth
          sx={{ mb: 2 }}
          label="Your Email (optional)"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your.email@example.com"
        />

        <TextField
          fullWidth
          multiline
          rows={4}
          label="Feedback Details"
          value={feedbackText}
          onChange={(e) => setFeedbackText(e.target.value)}
          placeholder="Please describe your feedback in detail..."
        />

        <div className="feedback-actions">
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!feedbackText.trim() || loading}
          >
            {loading ? <CircularProgress size={24} /> : "Submit Feedback"}
          </Button>
        </div>
      </div>
    </div>
  )
}

const Bot = ({
  botText,
  loading,
  conversationId,
  showFeedback,
  onFeedbackSubmit,
  isSubmittingFeedback,
  feedbackMessage,
  messageId
}) => {
  const [showFeedbackForm, setShowFeedbackForm] = React.useState(false)
  const [showThumbsUpFeedback, setShowThumbsUpFeedback] = React.useState(false)

  const handleFeedbackSubmit = (feedbackData) => {
    onFeedbackSubmit(feedbackData)
    setShowFeedbackForm(false)
  }

  const handlePositiveFeedback = async () => {
    try {
      await sendPositiveFeedback(conversationId, messageId)
      setShowThumbsUpFeedback(true)
      setTimeout(() => setShowThumbsUpFeedback(false), 2000)
    } catch (error) {
      console.error("Failed to submit positive feedback:", error)
    }
  }

  return (
    <div className="div-vertical">
      <div className="bot">
        <img className="bot-logo" src={`${process.env.PUBLIC_URL}${"assets/bot.png"}`} alt="bot" />
        {loading && botText === "" && <CircularProgress size={20} />}
        <div className="bot-text text-area">
          <ReactMarkdown
            children={botText}
            components={{ a: LinkRenderer }}
            rehypePlugins={[rehypeRaw]}
          />
          {showFeedback && (
            <div className="feedback-icons-container">
              <FaThumbsUp
                className="thumbs-icon thumbs-up-icon"
                color={showThumbsUpFeedback ? "#00ff00" : "#888"}
                size={18}
                onClick={handlePositiveFeedback}
              />
              <FaThumbsDown
                className="thumbs-icon thumbs-down-icon"
                color="#888"
                size={18}
                onClick={() => setShowFeedbackForm(true)}
              />
            </div>
          )}
        </div>
      </div>

      {(feedbackMessage || showThumbsUpFeedback) && (
        <Alert
          severity={showThumbsUpFeedback ? "success" : feedbackMessage?.type}
          sx={{ marginTop: 1, marginLeft: 5 }}
        >
          {showThumbsUpFeedback ? "Thanks for your feedback!" : feedbackMessage?.text}
        </Alert>
      )}

      {showFeedbackForm && (
        <FeedbackForm
          onClose={() => setShowFeedbackForm(false)}
          onSubmit={handleFeedbackSubmit}
          loading={isSubmittingFeedback}
          error={feedbackMessage?.type === "error" ? feedbackMessage.text : null}
        />
      )}
    </div>
  )
}

export default Bot
